/**
 * Colours for team chips.
 *
 * Teams cannot use DROPDOWN_COLORS: that map is keyed by hardcoded uuid, so
 * extending it for a team would mean a code deploy every time somebody creates
 * one. The colour lives on the row instead (`teams.color`), and this module is
 * the two things a stored colour still needs — a palette to pick from, and a
 * deterministic fallback for a row that somehow has no usable value.
 *
 * Rendering itself reuses ColorBadge / textColorForBg / borderColorForBg. There
 * is no new colour maths here.
 */

/**
 * The palette offered when creating or editing a team.
 *
 * Chosen so textColorForBg()'s luminance threshold (150) lands cleanly on each
 * one — no entry sits near the boundary where white-vs-black text is a coin
 * flip — and so adjacent entries are far enough apart in hue to tell apart at
 * chip size.
 */
export const TEAM_PALETTE: readonly string[] = [
  "#3631ca", // indigo
  "#0f766e", // teal
  "#b45309", // amber
  "#9d174d", // rose
  "#4d7c0f", // lime
  "#6d28d9", // violet
  "#0369a1", // sky
  "#b91c1c", // red
  "#115e59", // deep teal
  "#7c2d12", // brick
] as const;

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * A stable palette entry for an id, used when a team has no valid stored colour.
 *
 * FNV-1a rather than a sum of char codes: uuids share most of their alphabet and
 * differ in a few positions, so a naive sum clusters badly and neighbouring
 * teams come out the same colour. Deterministic on purpose — the same team is
 * always the same colour, across machines and reloads, with no storage.
 */
export function paletteFor(id: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    // 16777619, via shifts: Math.imul keeps this a 32-bit multiply.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return TEAM_PALETTE[hash % TEAM_PALETTE.length];
}

/** The colour to render a team in: its own, or a stable fallback. */
export function teamColor(team: { id: string; color?: string | null }): string {
  return team.color && HEX.test(team.color) ? team.color : paletteFor(team.id);
}

/**
 * The colour to assign a newly created team.
 *
 * Walks the palette by how many teams already exist, so a fresh tenant gets
 * visibly distinct teams without anybody choosing anything.
 */
export function nextPaletteColor(existingTeamCount: number): string {
  return TEAM_PALETTE[existingTeamCount % TEAM_PALETTE.length];
}
